import { Alert, Button, Paper, Switch, TextField, Typography } from '@mui/material'
import { Controller, useForm } from 'react-hook-form'

import { Box } from '@/components/ui'
import {
  type FeatureFlag,
  MAX_FLAG_DESCRIPTION_LENGTH,
  type UpdateFeatureFlagRequest,
} from '@/definitions/featureFlags'
import { useConfirm } from '@/hooks/useConfirm'
import { useSnackbar } from '@/hooks/useSnackbar'
import { useUpdateFeatureFlag } from '@/hooks/useUpdateFeatureFlag'
import { getApiErrorMessage } from '@/utils/apiErrors'

import { allOrgsChange } from '../../utils/allOrgsChange'

const SAVE_FAILED_MESSAGE = 'These changes could not be saved.'

const NOT_LIVE_MESSAGE =
  'This flag is switched off in the database, so it reaches nobody it has not ' +
  'already been granted to individually. Turning it back on is a database change.'

/**
 * In rollout order: a flag is normally given to new practices first and opened
 * to everyone later.
 */
const SWITCHES: { field: keyof UpdateFeatureFlagRequest; label: string; hint: string }[] = [
  {
    field: 'enable_new_orgs',
    label: 'On for new orgs',
    hint: 'Grants the flag to newly created organizations.',
  },
  {
    field: 'enable_all_orgs',
    label: 'On for all orgs',
    hint: 'Turns the flag on for every organization.',
  },
]

const buildFormValues = (flag: FeatureFlag): UpdateFeatureFlagRequest => ({
  enable_new_orgs: flag.enable_new_orgs,
  enable_all_orgs: flag.enable_all_orgs,
  description: flag.description,
})

interface GlobalSwitchesFormProps {
  flag: FeatureFlag
}

/**
 * A flag's rollout switches, in the order a rollout usually goes.
 *
 * Nothing writes as it is clicked — the switches stage against the stored values
 * and one save applies the set, because the endpoint replaces the whole set
 * rather than patching a field. `enable_all_orgs` moves every practice at once in
 * either direction, so a staged change to it is called out before the save and
 * asked about during it.
 *
 * The flag's own `enabled` column is never offered as a control: withdrawing a
 * flag as a concept is a database change, and a switch for it here would sit
 * alongside the rollout switches inviting a much larger action than the one
 * intended. When it is off it takes the switches' place with a note saying so,
 * since neither switch reaches anybody while it is. A per-organization grant
 * still does, here and on the rollout panel.
 */
export const GlobalSwitchesForm = ({ flag }: GlobalSwitchesFormProps) => {
  const confirm = useConfirm()
  const { showSuccess } = useSnackbar()
  const { mutateAsync, isPending, error } = useUpdateFeatureFlag()

  const { control, formState, handleSubmit, register, reset, watch } =
    useForm<UpdateFeatureFlagRequest>({
      defaultValues: buildFormValues(flag),
      // Stated rather than left to the default, because this form depends on it:
      // the two switches are not rendered while the flag is off, and the endpoint
      // requires both fields. Unregistering on unmount would drop them from the
      // payload and the save would be rejected.
      shouldUnregister: false,
    })

  const saveError = error === null ? null : getApiErrorMessage(error, SAVE_FAILED_MESSAGE)

  // The banner reads the switch through the same function the save does, so the
  // two cannot come to disagree about which direction is being applied.
  const stagedAllOrgs = allOrgsChange(flag, watch('enable_all_orgs'))

  const onSubmit = handleSubmit(async (staged) => {
    const change = allOrgsChange(flag, staged.enable_all_orgs)

    if (change === 'TURNING_ON') {
      const confirmed = await confirm({
        title: `Turn ${flag.name} on for every organization?`,
        body: (
          <Typography variant='body2'>
            This will immediately enable the feature for every organization.
          </Typography>
        ),
        confirmLabel: 'Turn on for all orgs',
      })
      if (!confirmed) {
        return
      }
    }

    if (change === 'TURNING_OFF') {
      const confirmed = await confirm({
        title: `Turn ${flag.name} off for every organization?`,
        body: (
          <Typography variant='body2'>
            Every organization that was not granted this flag directly loses the
            feature immediately. Only the ones holding a grant of their own keep
            it.
          </Typography>
        ),
        confirmLabel: 'Turn off for all orgs',
        destructive: true,
      })
      if (!confirmed) {
        return
      }
    }

    const stored = await mutateAsync({ name: flag.name, data: staged })
    reset(buildFormValues(stored))
    showSuccess('Feature flag updated.')
  })

  return (
    <Paper variant='detailCard'>
      <Box variant='flexColumn' gap='lg'>
        <Typography variant='h5'>{flag.name}</Typography>

        {saveError !== null && <Alert severity='error'>{saveError}</Alert>}

        <TextField
          label='Description'
          fullWidth
          multiline
          error={formState.errors.description !== undefined}
          helperText={formState.errors.description?.message}
          {...register('description', {
            // Both of the server's constraints, so neither comes back as a raw
            // 422. The message is what the field shows: a save blocked by
            // validation never reaches the error alert.
            maxLength: {
              value: MAX_FLAG_DESCRIPTION_LENGTH,
              message: `A description can be at most ${MAX_FLAG_DESCRIPTION_LENGTH} characters.`,
            },
            validate: (value) =>
              value.trim().length > 0 || 'A description is required.',
          })}
        />

        {/* The switches are stated to be unreachable rather than shown disabled:
            neither one changes who gets a flag that is switched off, so offering
            them — even greyed — invites a rollout that would appear to do nothing.
            The fields stay in the form and are still saved, so the values survive
            the flag being turned back on. */}
        {flag.enabled ? (
          SWITCHES.map(({ field, label, hint }) => (
            <Box key={field} variant='flexBetween'>
              <Box variant='flexColumn' gap='none' fullWidth={false}>
                <Typography variant='subtitle2'>{label}</Typography>
                <Typography variant='caption' color='text.secondary'>
                  {hint}
                </Typography>
              </Box>
              <Controller
                name={field}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Switch
                    checked={Boolean(value)}
                    slotProps={{ input: { 'aria-label': label } }}
                    onChange={(event) => onChange(event.target.checked)}
                  />
                )}
              />
            </Box>
          ))
        ) : (
          <Alert severity='warning'>{NOT_LIVE_MESSAGE}</Alert>
        )}

        {/* Said before the save rather than only in its dialog, since this is the
            one switch whose reach is every practice at once. */}
        {stagedAllOrgs === 'TURNING_ON' && (
          <Alert severity='info'>
            This will make the feature generally available for all orgs.
          </Alert>
        )}

        {stagedAllOrgs === 'TURNING_OFF' && (
          <Alert severity='warning'>
            This will immediately remove the feature from every organization that
            was not granted it directly.
          </Alert>
        )}

        <Box variant='flex' gap='md' fullWidth={false}>
          <Button
            variant='contained'
            disabled={!formState.isDirty || isPending}
            onClick={onSubmit}
          >
            {isPending ? 'Saving...' : 'Save'}
          </Button>
          <Button
            variant='outlined'
            disabled={!formState.isDirty || isPending}
            onClick={() => reset()}
          >
            Reset to saved
          </Button>
        </Box>
      </Box>
    </Paper>
  )
}
